import { useCallback, useEffect, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./SilveredPaywall.module.scss"

/**
 * SilveredPaywall (`/silvered`) — an original, self-contained FEATURE-SPECIFIC
 * paywall for Private Photo Vault Premium. Paywall type rolled at random this
 * run: FEATURE-SPECIFIC (a screen that heroes one real capability).
 *
 * Divergence brief + full axes: claude-paywalls/_ledger.md. Concept doc:
 * claude-paywalls/silvered.md.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * Newest @Paywalls-Bot mention this run: `focus on security and privacy` (NOT
 * the reset keyword `default`) → an ACTIVE STANDING DIRECTIVE at super-high
 * priority. The whole concept is built to it: the pitch is SECURITY & PRIVACY,
 * framed as the one property that makes a vault a vault — one-way visibility.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "You see everything. No one else sees in." Your vault behaves like a pane of
 * ONE-WAY GLASS. From your side it's a clear window onto everything you love —
 * every memory, in full colour. From the outside — the moment anyone else picks
 * up your phone — the very same pane is just a MIRROR: it shows them their own
 * reflection and nothing of yours. The hero is that pane. A live "Outside" inset
 * proves the asymmetry: it stays a drifting silver mirror with a faint onlooker
 * reflection, and never once reveals a photo, no matter what you do on your side.
 * You press "Look inside" (or the auto-demo does) and YOUR side clears to the
 * memory wall while theirs stays mirrored; "Keep my side clear" settles it.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the security cluster) ─────
 * Hero motif  : a ONE-WAY MIRROR / silvered pane — no prior concept is a mirror.
 * Value frame : ASYMMETRIC VISIBILITY — you-see-in / no-one-sees-through. Not
 *               concealment-by-removal (Lodestone/Offscreen), not sole-knowledge
 *               (OnlyYou), not disguise-as-another-app (PlainSight), not a
 *               before/after contrast (Sightline/Original).
 * Layout      : a portrait GLASS PANE on a gallery-at-dusk stage with a pinned
 *               live "Outside view" proof inset. Not a keypad screen (OnlyYou),
 *               not a springboard (PlainSight), not a spatial field (Lodestone).
 * Interaction : two simultaneous truths held on one object — you light YOUR side
 *               (press-and-hold "Look inside"); the outside face is bound to the
 *               same pane yet can never clear. The proof is the dual view, not a
 *               single reveal.
 * Register    : intimate, poised, quietly magical — memories always exist and
 *               are always yours; the drama is purely who can SEE them.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * The one-way mirror is an honest metaphor for the vault's privacy, mapped to
 * REAL Private Photo Vault features named in the copy: the only-you lock (Face
 * ID / Touch ID / PIN), keeping photos out of the Camera Roll & shared albums,
 * and a private encrypted backup only you can open. The memory wall is labelled
 * a demo library, not the user's photos. No data-loss-on-expiration claim
 * (privacy is about who can see in, never about losing photos), no fake urgency
 * or scarcity, honest "Save %" from live product data. The onlooker is an
 * abstract silhouette reflection, never a real face. Yearly is the featured,
 * default, most prominent price; monthly is a small secondary line.
 */

type PlanKey = "yearly" | "monthly"

// Reuse existing bundled personal-memory photos for the wall behind the glass.
// Shown in full colour throughout — the point is never to hide them from you,
// only that no one ELSE can see through to them.
const MEMORIES = ["m02", "m05", "m08", "m11", "m14", "m17", "m03", "m09"]

// What the one-way glass actually is, in real terms — real vault features.
const PROOFS = [
  {
    k: "lock",
    label: "Locked to you alone — Face ID, Touch ID or PIN",
  },
  {
    k: "roll",
    label: "Kept out of your Camera Roll & shared albums",
  },
  {
    k: "backup",
    label: "Private encrypted backup only you can open",
  },
]

const SilveredPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = (() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 79
    return Math.round((1 - y / (m * 12)) * 100)
  })()

  // clear = your side is currently a clear window; sealed = you settled it that way
  const [clear, setClear] = useState<boolean>(!!reduceMotion)
  const [sealed, setSealed] = useState<boolean>(!!reduceMotion)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)
  const [hint, setHint] = useState(false)
  const [tilt, setTilt] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

  const touched = useRef(false)
  const sealedRef = useRef(sealed)
  const timers = useRef<number[]>([])
  const revertTimer = useRef<number>(0)
  const paneRef = useRef<HTMLDivElement>(null)
  sealedRef.current = sealed

  const pushTimer = (id: number) => timers.current.push(id)

  // --- the "look inside" action: light your side; the outside face never clears
  const lightMySide = useCallback(() => {
    if (revertTimer.current) window.clearTimeout(revertTimer.current)
    setClear(true)
    native.logMixpanelEvent("html_silvered_look")
  }, [])

  const releaseMySide = useCallback(() => {
    if (sealedRef.current) return
    // your side only stays clear while you're looking — reinforces "only you"
    if (revertTimer.current) window.clearTimeout(revertTimer.current)
    revertTimer.current = window.setTimeout(() => {
      if (!sealedRef.current) setClear(false)
    }, 900)
  }, [])

  const seal = useCallback(() => {
    if (revertTimer.current) window.clearTimeout(revertTimer.current)
    sealedRef.current = true
    setSealed(true)
    setClear(true)
    native.logMixpanelEvent("html_silvered_seal")
  }, [])

  // Auto-demo for passive viewers: clear your side, hold, then settle it clear.
  useEffect(() => {
    if (reduceMotion) {
      setShowClose(true)
      return
    }
    pushTimer(window.setTimeout(() => setHint(true), 900))
    pushTimer(window.setTimeout(() => setShowClose(true), 2200))
    pushTimer(
      window.setTimeout(() => {
        if (!touched.current) lightMySide()
      }, 2000),
    )
    pushTimer(
      window.setTimeout(() => {
        if (!touched.current) seal()
      }, 3600),
    )
    return () => {
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
      if (revertTimer.current) window.clearTimeout(revertTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // subtle pointer parallax for depth behind the glass (ambient only)
  const onPaneMove = (e: React.PointerEvent) => {
    const el = paneRef.current
    if (!el || reduceMotion) return
    const r = el.getBoundingClientRect()
    const nx = (e.clientX - r.left) / r.width - 0.5
    const ny = (e.clientY - r.top) / r.height - 0.5
    setTilt({ x: nx, y: ny })
  }
  const onPaneLeave = () => setTilt({ x: 0, y: 0 })

  const beginLook = () => {
    touched.current = true
    setHint(false)
    lightMySide()
  }

  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct
  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    native.logMixpanelEvent("html_silvered_subscribe", { plan })
    if (selectedProduct) native.subscribe(selectedProduct)
  }
  const openTerms = () => native.postNativeEvent("terms")
  const openPrivacy = () => native.postNativeEvent("privacy")

  // the drifting onlooker reflection — an abstract silhouette on the mirror face
  const onlooker = (extraClass = "") => (
    <span className={`${styles.onlooker} ${extraClass}`} aria-hidden="true">
      <svg viewBox="0 0 80 80" width="100%" height="100%">
        <defs>
          <radialGradient id="olg" cx="50%" cy="34%" r="60%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.5" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx="40" cy="30" r="14" fill="url(#olg)" />
        <path d="M15 76c2-16 12-25 25-25s23 9 25 25z" fill="url(#olg)" />
      </svg>
    </span>
  )

  return (
    <div
      className={`${styles.root} ${clear ? styles.rootClear : ""} ${
        sealed ? styles.rootSealed : ""
      }`}
    >
      <div className={styles.stageGlow} aria-hidden="true" />

      {/* ---------- header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="11.4" r="2.1" fill="currentColor" />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- title ---------- */}
      <div className={styles.intro}>
        <span className={styles.eyebrow}>Feature · One-way glass</span>
        <h1 className={styles.title}>
          You see everything.
          <br />
          <span className={styles.titleAccent}>No one else sees in.</span>
        </h1>
        <p className={styles.sub}>
          Your vault is a pane of one-way glass. To you, a clear window onto
          every memory. To anyone else who picks up your phone — just a mirror.
        </p>
      </div>

      {/* ---------- the pane ---------- */}
      <div className={styles.stage}>
        <div
          ref={paneRef}
          className={`${styles.pane} ${hint ? styles.paneHint : ""}`}
          onPointerMove={onPaneMove}
          onPointerLeave={onPaneLeave}
          onPointerDown={beginLook}
          role="group"
          aria-label="Your vault, a one-way mirror"
        >
          {/* behind the glass — your memory wall (your side) */}
          <div
            className={styles.memories}
            style={
              {
                ["--px" as any]: `${tilt.x * 10}px`,
                ["--py" as any]: `${tilt.y * 8}px`,
              } as React.CSSProperties
            }
          >
            {MEMORIES.map((m, i) => (
              <span
                key={m}
                className={styles.memory}
                style={{ transitionDelay: `${clear ? i * 45 : 0}ms` }}
              >
                <span
                  className={styles.memoryImg}
                  style={{ backgroundImage: `url(/byheart/${m}.jpg)` }}
                />
              </span>
            ))}
            <span className={styles.memoryWarm} aria-hidden="true" />
          </div>

          {/* the silvered surface — the mirror everyone else meets */}
          <div className={styles.silver} aria-hidden="true">
            <span className={styles.silverSheen} />
            {onlooker(styles.onlookerMain)}
          </div>

          {/* glass edge + hairline */}
          <span className={styles.paneEdge} aria-hidden="true" />

          {/* status readout on the pane */}
          <div className={styles.readout} aria-live="polite">
            <span
              className={`${styles.readoutDot} ${clear ? styles.readoutDotOn : ""}`}
              aria-hidden="true"
            />
            <span className={styles.readoutText}>
              {sealed
                ? "Your window · sealed to you"
                : clear
                  ? "Clear — only from your side"
                  : "Locked · a mirror to everyone"}
            </span>
          </div>

          {/* the always-mirror proof inset — never reveals a photo */}
          <div className={styles.outside} aria-hidden="true">
            <span className={styles.outsideLabel}>What anyone else sees</span>
            <span className={styles.outsidePane}>
              <span className={styles.outsideSheen} />
              {onlooker(styles.onlookerMini)}
            </span>
            <span className={styles.outsideTag}>Mirror</span>
          </div>
        </div>

        {/* the look control */}
        <div className={styles.control}>
          {sealed ? (
            <span className={styles.settled}>
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5l4 4 10-10.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Your side stays a window. Theirs stays a mirror.
            </span>
          ) : (
            <div className={styles.controlRow}>
              <button
                className={`${styles.lookBtn} ${clear ? styles.lookBtnActive : ""}`}
                onPointerDown={(e) => {
                  e.preventDefault()
                  beginLook()
                }}
                onPointerUp={releaseMySide}
                onPointerLeave={releaseMySide}
                onPointerCancel={releaseMySide}
                aria-label="Look inside your vault"
              >
                <span className={styles.lookDot} aria-hidden="true" />
                {clear ? "You’re looking in" : "Hold to look inside"}
              </button>
              <button
                className={styles.keepBtn}
                onClick={seal}
                disabled={!!reduceMotion && sealed}
              >
                Keep my side clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ---------- proofs ---------- */}
      <div className={styles.proofs}>
        <span className={styles.proofsLead}>
          A one-way mirror is only how it feels. What it really is:
        </span>
        <div className={styles.proofList}>
          {PROOFS.map((p, i) => (
            <span
              key={p.k}
              className={`${styles.proof} ${sealed ? styles.proofOn : ""}`}
              style={{ transitionDelay: `${120 + i * 90}ms` }}
            >
              <svg
                viewBox="0 0 24 24"
                width="13"
                height="13"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5l4 4 10-10.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {p.label}
            </span>
          ))}
        </div>
      </div>

      {/* ---------- pricing (yearly featured) ---------- */}
      <div className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbon}>SAVE {savingsPct}%</span>
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planMeta}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                7 days free · then {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planRight}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planName}>Monthly</span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <motion.button
        className={styles.cta}
        onClick={handleSubscribe}
        whileTap={{ scale: 0.985 }}
      >
        <span className={styles.ctaMain}>
          {plan === "yearly" ? "Try 7 days free" : "Continue"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `Then ${yearlyPrice}/year · cancel anytime`
            : `${monthlyPrice}/month · cancel anytime`}
        </span>
      </motion.button>

      <div className={styles.legal}>
        <button onClick={openTerms}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={openPrivacy}>Privacy</button>
        <span aria-hidden="true">·</span>
        <span>Auto-renews until cancelled</span>
      </div>
    </div>
  )
}

export default SilveredPaywall
